import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Upload, X, ImagePlus, Loader2, Video, Play, Camera, Plus, Sparkles, Shield, Package, Search, Check, Warehouse } from 'lucide-react';
import CommissionSimulator from '../components/CommissionSimulator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'pieces', labelKey: 'createListing.category_pieces' },
  { value: 'voitures', labelKey: 'createListing.category_voitures' },
  { value: 'motos', labelKey: 'createListing.category_motos' },
  { value: 'utilitaires', labelKey: 'createListing.category_utilitaires' },
  { value: 'engins', labelKey: 'createListing.category_engins' },
  { value: 'accessoires', labelKey: 'createListing.category_accessoires' },
  { value: 'recherche', labelKey: 'createListing.category_recherche' },
  { value: 'rare', labelKey: 'createListing.category_rare' },
];

const conditions = [
  { value: 'neuf', labelKey: 'createListing.condition_neuf' },
  { value: 'occasion', labelKey: 'createListing.condition_occasion' },
  { value: 'reconditionne', labelKey: 'createListing.condition_reconditionne' },
];

export default function CreateListing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [piecesSubcategories, setPiecesSubcategories] = useState({});
  const [accessoiresSubcategories, setAccessoiresSubcategories] = useState({});
  const [motosSubcategories, setMotosSubcategories] = useState({});
  const [utilitairesSubcategories, setUtilitairesSubcategories] = useState({});
  const [enginsSubcategories, setEnginsSubcategories] = useState({});
  const [carBrands, setCarBrands] = useState([]);
  const [photoLimit, setPhotoLimit] = useState({ max_photos: 6, extra_credits: 0, is_pro: false });
  const [videoLimit, setVideoLimit] = useState({ max_duration: 30, max_size_mb: 30, is_extended: false });
  const [buyingPhotos, setBuyingPhotos] = useState(false);
  const [buyingVideo, setBuyingVideo] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // États pour la sélection d'article entrepôt
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [loadingWarehouse, setLoadingWarehouse] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [selectedWarehouseItem, setSelectedWarehouseItem] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    subcategory: '',
    condition: 'occasion',
    brand: '',
    model: '',
    year: '',
    mileage: '',
    location: '',
    postal_code: '',
    region: '',
    shipping_cost: '',
    shipping_info: '',
    shipping_methods: [], // Modes de livraison sélectionnés
    // Compatibilité
    compatible_brands: [],
    compatible_models: '',
    compatible_years: '',
    oem_reference: '',
    aftermarket_reference: '',
    // Confiance & Traçabilité
    part_origin: '',
    vehicle_mileage: '',
    // Quantité disponible
    quantity: '1',
    // Caractéristiques véhicule donneur
    fuel_type: '',        // Type de carburant
    gearbox: '',          // Type de boîte de vitesses
    vehicle_color: '',    // Couleur du véhicule
    color_code: '',       // Code couleur
    body_type: '',        // Type de carrosserie
    drive_type: '',       // Roues motrices
    steering_side: '',    // Position du volant
    vin_available: false, // VIN disponible
    vin_number: '',       // Numéro VIN
  });

  // Modèles populaires par marque
  const modelsByBrand = {
    'Audi': ['A3', 'A4', 'A5', 'A6', 'Q3', 'Q5', 'Q7', 'TT', 'RS3', 'RS4'],
    'BMW': ['Série 1', 'Série 3', 'Série 5', 'X1', 'X3', 'X5', 'M3', 'M5', 'E36', 'E46', 'E90'],
    'Citroën': ['C3', 'C4', 'C5', 'Berlingo', 'DS3', 'DS4', 'Picasso', 'Saxo', 'Xsara'],
    'Dacia': ['Sandero', 'Duster', 'Logan', 'Lodgy', 'Spring', 'Jogger'],
    'Fiat': ['500', 'Panda', 'Punto', 'Tipo', 'Doblo', 'Ducato'],
    'Ford': ['Fiesta', 'Focus', 'Mondeo', 'Kuga', 'Puma', 'Mustang', 'Transit'],
    'Honda': ['Civic', 'Accord', 'CR-V', 'Jazz', 'HR-V', 'NSX'],
    'Hyundai': ['i10', 'i20', 'i30', 'Tucson', 'Kona', 'Santa Fe'],
    'Kia': ['Picanto', 'Rio', 'Ceed', 'Sportage', 'Sorento', 'Stinger'],
    'Mercedes': ['Classe A', 'Classe C', 'Classe E', 'GLA', 'GLC', 'GLE', 'AMG'],
    'Nissan': ['Micra', 'Qashqai', 'Juke', 'X-Trail', 'Leaf', '350Z', 'GT-R'],
    'Opel': ['Corsa', 'Astra', 'Insignia', 'Mokka', 'Grandland', 'Zafira'],
    'Peugeot': ['208', '308', '508', '2008', '3008', '5008', 'Partner', '106', '206', '306'],
    'Renault': ['Clio', 'Megane', 'Captur', 'Kadjar', 'Scenic', 'Twingo', 'Kangoo', 'Trafic'],
    'Seat': ['Ibiza', 'Leon', 'Arona', 'Ateca', 'Tarraco'],
    'Skoda': ['Fabia', 'Octavia', 'Superb', 'Kodiaq', 'Karoq'],
    'Toyota': ['Yaris', 'Corolla', 'Auris', 'RAV4', 'C-HR', 'Supra', 'Hilux'],
    'Volkswagen': ['Polo', 'Golf', 'Passat', 'Tiguan', 'T-Roc', 'Touran', 'Transporter'],
    'Volvo': ['V40', 'V60', 'XC40', 'XC60', 'XC90', 'S60']
  };

  // Options de livraison disponibles
  const shippingOptions = [
    { value: 'hand_delivery', labelKey: 'createListing.shipping_hand_delivery', icon: '🤝', descKey: 'createListing.shipping_hand_delivery_desc' },
    { value: 'colissimo', labelKey: 'createListing.shipping_colissimo', icon: '📦', descKey: 'createListing.shipping_colissimo_desc' },
    { value: 'mondial_relay', labelKey: 'createListing.shipping_mondial_relay', icon: '🏪', descKey: 'createListing.shipping_mondial_relay_desc' },
    { value: 'chronopost', labelKey: 'createListing.shipping_chronopost', icon: '⚡', descKey: 'createListing.shipping_chronopost_desc' },
    { value: 'boxtal', labelKey: 'createListing.shipping_boxtal', icon: '🚚', descKey: 'createListing.shipping_boxtal_desc' },
    { value: 'custom', labelKey: 'createListing.shipping_custom', icon: '📋', descKey: 'createListing.shipping_custom_desc' },
  ];

  // Options caractéristiques véhicule donneur
  const fuelTypes = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'essence', label: 'Essence' },
    { value: 'essence_gpl', label: 'Essence / GPL' },
    { value: 'hybride', label: 'Hybride' },
    { value: 'electrique', label: 'Électrique' },
    { value: 'autre', label: 'Autre' },
  ];

  const gearboxTypes = [
    { value: 'manuel', label: 'Manuel' },
    { value: 'automatique', label: 'Automatique' },
  ];

  const vehicleColors = [
    { value: 'noir', label: 'Noir' },
    { value: 'blanc', label: 'Blanc' },
    { value: 'gris', label: 'Gris' },
    { value: 'bleu', label: 'Bleu' },
    { value: 'rouge', label: 'Rouge' },
    { value: 'vert', label: 'Vert' },
    { value: 'jaune', label: 'Jaune' },
    { value: 'orange', label: 'Orange' },
    { value: 'marron', label: 'Marron' },
    { value: 'violet', label: 'Violet' },
    { value: 'beige', label: 'Beige' },
    { value: 'argent', label: 'Argent' },
  ];

  const bodyTypes = [
    { value: 'berline', label: 'Berline' },
    { value: 'break', label: 'Break' },
    { value: 'hayon', label: 'Voiture à hayon' },
    { value: 'suv', label: 'SUV' },
    { value: 'coupe', label: 'Coupé' },
    { value: 'cabriolet', label: 'Cabriolet' },
    { value: 'monospace', label: 'Monospace' },
    { value: 'utilitaire', label: 'Utilitaire' },
    { value: 'pick-up', label: 'Pick-up' },
    { value: 'autre', label: 'Autre' },
  ];

  const driveTypes = [
    { value: 'avant', label: 'Traction avant' },
    { value: 'arriere', label: 'Propulsion arrière' },
    { value: '4x4', label: 'Quatre roues motrices' },
  ];

  const steeringSides = [
    { value: 'gauche', label: 'Gauche (LHD)' },
    { value: 'droite', label: 'Droite (RHD)' },
  ];

  const regions = [
    { value: 'ile-de-france', label: 'Île-de-France' },
    { value: 'auvergne-rhone-alpes', label: 'Auvergne-Rhône-Alpes' },
    { value: 'nouvelle-aquitaine', label: 'Nouvelle-Aquitaine' },
    { value: 'occitanie', label: 'Occitanie' },
    { value: 'hauts-de-france', label: 'Hauts-de-France' },
    { value: 'paca', label: 'Provence-Alpes-Côte d\'Azur' },
    { value: 'grand-est', label: 'Grand Est' },
    { value: 'pays-de-la-loire', label: 'Pays de la Loire' },
    { value: 'bretagne', label: 'Bretagne' },
    { value: 'normandie', label: 'Normandie' },
    { value: 'bourgogne-franche-comte', label: 'Bourgogne-Franche-Comté' },
    { value: 'centre-val-de-loire', label: 'Centre-Val de Loire' },
    { value: 'corse', label: 'Corse' },
  ];

  useEffect(() => {
    fetchSubcategories();
    fetchCarBrands();
    fetchPhotoLimit();
    fetchVideoLimit();
    
    // Handle photo purchase success/cancel
    if (searchParams.get('photos_success') === 'true') {
      toast.success(t('createListing.photos_success'));
      fetchPhotoLimit();
      refreshUser();
    } else if (searchParams.get('photos_cancelled') === 'true') {
      toast.info(t('createListing.photos_cancelled'));
    }
    
    // Handle video purchase success/cancel
    if (searchParams.get('video_success') === 'true') {
      toast.success(t('createListing.video_success'));
      fetchVideoLimit();
      refreshUser();
    } else if (searchParams.get('video_cancelled') === 'true') {
      toast.info(t('createListing.video_cancelled'));
    }
  }, [searchParams]);

  // Charger les articles de l'entrepôt
  const fetchWarehouseItems = async () => {
    setLoadingWarehouse(true);
    try {
      const response = await axios.get(`${API}/warehouse/items`);
      // Filtrer uniquement les articles avec stock > 0 et non déjà liés à une annonce
      const availableItems = response.data.filter(item => item.quantity > 0 && !item.listing_id);
      setWarehouseItems(availableItems);
    } catch (error) {
      console.error('Error fetching warehouse items:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoadingWarehouse(false);
    }
  };

  // Ouvrir le modal de sélection d'article entrepôt
  const openWarehouseModal = () => {
    fetchWarehouseItems();
    setShowWarehouseModal(true);
  };

  // Sélectionner un article et pré-remplir le formulaire
  const handleSelectWarehouseItem = (item) => {
    setSelectedWarehouseItem(item);
    setFormData(prev => ({
      ...prev,
      title: item.name || '',
      description: item.notes || '',
      price: item.selling_price?.toString() || '',
      condition: item.condition || 'occasion',
      brand: item.brand || '',
      oem_reference: item.reference_oem || '',
      compatible_models: item.compatible_vehicles || '',
    }));
    setShowWarehouseModal(false);
    toast.success(t('createListing.warehouse_item_selected_toast'));
  };

  // Retirer l'article entrepôt sélectionné
  const clearWarehouseItem = () => {
    setSelectedWarehouseItem(null);
    toast.info(t('createListing.warehouse_item_removed'));
  };

  // Filtrer les articles entrepôt par recherche
  const filteredWarehouseItems = warehouseItems.filter(item => 
    item.name?.toLowerCase().includes(warehouseSearch.toLowerCase()) ||
    item.reference_oem?.toLowerCase().includes(warehouseSearch.toLowerCase()) ||
    item.brand?.toLowerCase().includes(warehouseSearch.toLowerCase())
  );

  const fetchPhotoLimit = async () => {
    try {
      const response = await axios.get(`${API}/users/me/photo-limit`);
      setPhotoLimit(response.data);
    } catch (error) {
      console.error('Error fetching photo limit:', error);
    }
  };

  const fetchVideoLimit = async () => {
    try {
      const response = await axios.get(`${API}/users/me/video-limit`);
      setVideoLimit(response.data);
    } catch (error) {
      console.error('Error fetching video limit:', error);
    }
  };

  const handleBuyExtraPhotos = async () => {
    setBuyingPhotos(true);
    try {
      const response = await axios.post(`${API}/photos/create-checkout-session`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erreur lors de la création du paiement');
      setBuyingPhotos(false);
    }
  };

  const handleBuyExtendedVideo = async () => {
    setBuyingVideo(true);
    try {
      const response = await axios.post(`${API}/video/create-checkout-session`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating video checkout:', error);
      toast.error('Erreur lors de la création du paiement');
      setBuyingVideo(false);
    }
  };

  const maxPhotos = photoLimit.max_photos || 6;

  const fetchSubcategories = async () => {
    try {
      const [piecesRes, accessoiresRes, motosRes, utilitairesRes, enginsRes] = await Promise.all([
        axios.get(`${API}/subcategories/pieces`),
        axios.get(`${API}/subcategories/accessoires`),
        axios.get(`${API}/subcategories/motos`),
        axios.get(`${API}/subcategories/utilitaires`),
        axios.get(`${API}/subcategories/engins`)
      ]);
      setPiecesSubcategories(piecesRes.data);
      setAccessoiresSubcategories(accessoiresRes.data);
      setMotosSubcategories(motosRes.data);
      setUtilitairesSubcategories(utilitairesRes.data);
      setEnginsSubcategories(enginsRes.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchCarBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setCarBrands(response.data);
    } catch (error) {
      console.error('Error fetching car brands:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCompatibleBrand = (brand) => {
    setFormData(prev => {
      const brands = prev.compatible_brands.includes(brand)
        ? prev.compatible_brands.filter(b => b !== brand)
        : [...prev.compatible_brands, brand];
      return { ...prev, compatible_brands: brands };
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxPhotos = photoLimit.max_photos || 5;
    
    if (images.length + files.length > maxPhotos) {
      toast.error(t('createListing.max_photos_error', { max: maxPhotos }));
      return;
    }

    setUploadingImages(true);
    const token = localStorage.getItem('token');

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        setImages(prev => [...prev, {
          url: response.data.url,
          public_id: response.data.public_id
        }]);
      } catch (error) {
        toast.error(`Erreur lors de l'upload: ${file.name}`);
        console.error('Upload error:', error);
      }
    }
    setUploadingImages(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = async (index) => {
    const image = images[index];
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/upload/image/${encodeURIComponent(image.public_id)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Delete error:', error);
    }
    setImages(images.filter((_, i) => i !== index));
  };

  // Video upload handler
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size based on user's limit
    const maxSizeMb = videoLimit.max_size_mb || 30;
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(t('createListing.video_size_error', { size: maxSizeMb }));
      return;
    }

    // Check file type
    if (!file.type.startsWith('video/')) {
      toast.error(t('createListing.video_type_error'));
      return;
    }

    setUploadingVideo(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/video`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setVideoUrl(response.data.url);
      
      // Refresh video limit if extended credit was used
      if (response.data.used_extended) {
        fetchVideoLimit();
      }
      
      toast.success(t('createListing.video_uploaded'));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'upload de la vidéo';
      toast.error(errorMsg);
      console.error('Video upload error:', error);
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const removeVideo = () => {
    setVideoUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier si l'utilisateur a des crédits OU des annonces gratuites
    const hasCredits = (user?.credits || 0) > 0;
    const hasFreeAds = (user?.free_ads_remaining || 0) > 0;
    
    if (!hasCredits && !hasFreeAds) {
      toast.error(t('createListing.no_credits_redirect'));
      navigate('/tarifs');
      return;
    }

    if (!formData.title || !formData.description || !formData.price || !formData.category) {
      toast.error(t('createListing.required_fields_error'));
      return;
    }

    if (formData.shipping_methods.length === 0) {
      toast.error(t('createListing.shipping_method_required'));
      return;
    }

    setLoading(true);
    try {
      const imageUrls = images.map(img => img.url);
      const compatible_models = formData.compatible_models 
        ? formData.compatible_models.split(',').map(m => m.trim()).filter(m => m)
        : [];
      
      const response = await axios.post(`${API}/listings`, {
        ...formData,
        price: parseFloat(formData.price),
        shipping_cost: formData.shipping_cost ? parseFloat(formData.shipping_cost) : null,
        shipping_methods: formData.shipping_methods,
        year: formData.year ? parseInt(formData.year) : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        vehicle_mileage: formData.vehicle_mileage ? parseInt(formData.vehicle_mileage) : null,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1,
        compatible_models,
        images: imageUrls,
        video_url: videoUrl || null,
        warehouse_item_id: selectedWarehouseItem?.id || null,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      await refreshUser();
      toast.success(t('createListing.publish_success'));
      navigate(`/annonce/${response.data.id}`);
    } catch (error) {
      console.error('Erreur création annonce:', error);
      if (error.response?.status === 402) {
        toast.error(t('createListing.credits_insufficient'));
        navigate('/tarifs');
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Action non autorisée');
      } else {
        toast.error(error.response?.data?.detail || t('createListing.publish_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const showVehicleFields = ['voitures', 'motos', 'utilitaires'].includes(formData.category);

  return (
    <div className="min-h-screen bg-secondary/30 py-8" data-testid="create-listing-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/tableau-de-bord" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          {t('createListing.back_to_dashboard')}
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center justify-between">
              {t('createListing.page_title')}
              <span className="text-sm font-normal text-muted-foreground">
                {(user?.free_ads_remaining || 0) > 0 ? (
                  <span>{t('createListing.free_ads_remaining')}: <span className="font-bold text-green-600">{user.free_ads_remaining}</span></span>
                ) : (
                  <span>{t('createListing.credits_remaining')}: <span className="font-bold text-accent">{user?.credits || 0}</span></span>
                )}
              </span>
            </CardTitle>
            {user?.is_professional && (
              <div className="mb-6 p-4 bg-accent/10 border border-accent/30 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-accent">💼 {t('createListing.pro_space_title')}</h3>
                    <p className="text-sm text-muted-foreground">{t('createListing.pro_space_desc')}</p>
                  </div>
                  <Link to="/entrepot">
                    <Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
                      {t('createListing.my_warehouse')}
                    </Button>
                  </Link>
                </div>
                
                {/* Sélection d'article entrepôt */}
                {selectedWarehouseItem ? (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg" data-testid="selected-warehouse-item">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-800 flex items-center justify-center">
                          <Package className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800 dark:text-green-200">{selectedWarehouseItem.name}</p>
                          <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                            {selectedWarehouseItem.reference_oem && <span>OEM: {selectedWarehouseItem.reference_oem}</span>}
                            <span>Stock: {selectedWarehouseItem.quantity}</span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearWarehouseItem}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid="clear-warehouse-item-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      {t('createListing.warehouse_stock_note')}
                    </p>
                  </div>
                ) : (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={openWarehouseModal}
                    className="w-full border-dashed border-accent/50 text-accent hover:bg-accent/5"
                    data-testid="select-warehouse-item-btn"
                  >
                    <Warehouse className="w-4 h-4 mr-2" />
                    {t('createListing.select_warehouse_item')}
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <div className="space-y-2">
                <Label>{t('createListing.category_label')} *</Label>
                <Select value={formData.category} onValueChange={(v) => { handleChange('category', v); handleChange('subcategory', ''); }}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={t('createListing.category_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{t(cat.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategory for Pieces */}
              {formData.category === 'pieces' && Object.keys(piecesSubcategories).length > 0 && (
                <div className="space-y-2">
                  <Label>{t('createListing.subcategory_pieces')} *</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder={t('createListing.subcategory_pieces_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(piecesSubcategories).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Subcategory for Accessoires */}
              {formData.category === 'accessoires' && Object.keys(accessoiresSubcategories).length > 0 && (
                <div className="space-y-2">
                  <Label>{t('createListing.subcategory_accessoires')} *</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder={t('createListing.subcategory_accessoires_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(accessoiresSubcategories).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.category === 'motos' && Object.keys(motosSubcategories).length > 0 && (
                <div className="space-y-2">
                  <Label>{t('createListing.subcategory_motos')} *</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder={t('createListing.subcategory_motos_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(motosSubcategories).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.category === 'utilitaires' && Object.keys(utilitairesSubcategories).length > 0 && (
                <div className="space-y-2">
                  <Label>{t('createListing.subcategory_utilitaires')} *</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder={t('createListing.subcategory_utilitaires_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(utilitairesSubcategories).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.category === 'engins' && Object.keys(enginsSubcategories).length > 0 && (
                <div className="space-y-2">
                  <Label>{t('createListing.subcategory_engins')} *</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => handleChange('subcategory', v)}>
                    <SelectTrigger data-testid="subcategory-engins-select">
                      <SelectValue placeholder={t('createListing.subcategory_engins_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(enginsSubcategories).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">{t('createListing.title_label')} *</Label>
                <Input
                  id="title"
                  placeholder={t('createListing.title_placeholder')}
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  data-testid="title-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">{t('createListing.description_label')} *</Label>
                <Textarea
                  id="description"
                  placeholder={t('createListing.description_placeholder')}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={5}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Price & Condition */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">{t('createListing.price_label')} *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    required
                    data-testid="price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('createListing.condition_label')} *</Label>
                  <Select value={formData.condition} onValueChange={(v) => handleChange('condition', v)}>
                    <SelectTrigger data-testid="condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {conditions.map(cond => (
                        <SelectItem key={cond.value} value={cond.value}>{t(cond.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantité disponible</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    placeholder="1"
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    data-testid="quantity-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Nombre de pièces identiques disponibles. Si = 1, le badge &quot;Dernière pièce&quot; s&apos;affichera.
                  </p>
                </div>
              </div>

              {/* Commission Simulator */}
              {formData.price && parseFloat(formData.price) > 0 && (
                <CommissionSimulator 
                  price={formData.price} 
                  shippingCost={formData.shipping_cost}
                />
              )}

              {/* Shipping / Livraison */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <h3 className="font-medium text-blue-900">📦 {t('createListing.shipping_title')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shipping_cost">{t('createListing.shipping_cost_label')}</Label>
                    <Input
                      id="shipping_cost"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('createListing.shipping_cost_placeholder')}
                      value={formData.shipping_cost}
                      onChange={(e) => handleChange('shipping_cost', e.target.value)}
                      data-testid="shipping-cost-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('createListing.shipping_cost_hint')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipping_info">{t('createListing.shipping_info_label')}</Label>
                    <Input
                      id="shipping_info"
                      placeholder={t('createListing.shipping_info_placeholder')}
                      value={formData.shipping_info}
                      onChange={(e) => handleChange('shipping_info', e.target.value)}
                      data-testid="shipping-info-input"
                    />
                  </div>
                </div>
                
                {/* Shipping Methods Selection */}
                <div className="mt-4">
                  <Label className="mb-2 block">{t('createListing.shipping_methods_label')} *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {shippingOptions.map((option) => {
                      const isSelected = formData.shipping_methods.includes(option.value);
                      return (
                        <div
                          key={option.value}
                          onClick={() => {
                            const newMethods = isSelected
                              ? formData.shipping_methods.filter(m => m !== option.value)
                              : [...formData.shipping_methods, option.value];
                            handleChange('shipping_methods', newMethods);
                          }}
                          className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                              : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                          }`}
                          data-testid={`shipping-method-${option.value}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{option.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'}`}>
                                {t(option.labelKey)}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{t(option.descKey)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('createListing.shipping_methods_hint')}
                  </p>
                </div>
              </div>

              {/* Vehicle-specific fields */}
              {showVehicleFields && (
                <div className="p-4 bg-secondary/50 rounded-lg space-y-4 animate-fade-in">
                  <h3 className="font-medium">{t('createListing.vehicle_info_title')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="brand">{t('createListing.brand_label')}</Label>
                      <Input
                        id="brand"
                        placeholder={t('createListing.brand_placeholder')}
                        value={formData.brand}
                        onChange={(e) => handleChange('brand', e.target.value)}
                        data-testid="brand-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="model">{t('createListing.model_label')}</Label>
                      <Input
                        id="model"
                        placeholder={t('createListing.model_placeholder')}
                        value={formData.model}
                        onChange={(e) => handleChange('model', e.target.value)}
                        data-testid="model-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="year">{t('createListing.year_label')}</Label>
                      <Input
                        id="year"
                        type="number"
                        placeholder="2020"
                        value={formData.year}
                        onChange={(e) => handleChange('year', e.target.value)}
                        data-testid="year-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mileage">{t('createListing.mileage_label')}</Label>
                      <Input
                        id="mileage"
                        type="number"
                        placeholder="50000"
                        value={formData.mileage}
                        onChange={(e) => handleChange('mileage', e.target.value)}
                        data-testid="mileage-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Brand/Model for parts */}
              {formData.category === 'pieces' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="brand">{t('createListing.brand_vehicle_label')}</Label>
                    <Input
                      id="brand"
                      placeholder={t('createListing.brand_placeholder')}
                      value={formData.brand}
                      onChange={(e) => handleChange('brand', e.target.value)}
                      data-testid="brand-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">{t('createListing.model_compatible_label')}</Label>
                    <Input
                      id="model"
                      placeholder={t('createListing.model_placeholder')}
                      value={formData.model}
                      onChange={(e) => handleChange('model', e.target.value)}
                      data-testid="model-input"
                    />
                  </div>
                </div>
              )}

              {/* Compatibility Section - for pieces and accessoires */}
              {(formData.category === 'pieces' || formData.category === 'accessoires') && (
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-4">
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    <span className="text-blue-600">🔧</span>
                    {t('createListing.compatibility_title')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('createListing.compatibility_hint')}
                  </p>
                  
                  {/* Compatible Brands */}
                  <div className="space-y-2">
                    <Label>{t('createListing.compatible_brands_label')}</Label>
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                      {carBrands.slice(0, -1).map((brand) => (
                        <button
                          key={brand}
                          type="button"
                          onClick={() => toggleCompatibleBrand(brand)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            formData.compatible_brands.includes(brand)
                              ? 'bg-accent text-accent-foreground'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                          data-testid={`brand-tag-${brand}`}
                        >
                          {brand}
                        </button>
                      ))}
                    </div>
                    {formData.compatible_brands.length > 0 && (
                      <p className="text-sm text-accent">
                        {formData.compatible_brands.length} {t('createListing.compatible_brands_selected')}
                      </p>
                    )}
                  </div>

                  {/* Compatible Models - Suggestions based on selected brands */}
                  <div className="space-y-2">
                    <Label htmlFor="compatible_models">{t('createListing.compatible_models_label')}</Label>
                    
                    {/* Quick model suggestions based on selected brands */}
                    {formData.compatible_brands.length > 0 && (
                      <div className="space-y-3 mb-3">
                        {formData.compatible_brands.map(brand => (
                          modelsByBrand[brand] && (
                            <div key={brand} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <p className="text-xs font-semibold text-blue-800 mb-2">{brand} - Modèles populaires :</p>
                              <div className="flex flex-wrap gap-1">
                                {modelsByBrand[brand].map(model => {
                                  const fullModel = `${brand} ${model}`;
                                  const isSelected = formData.compatible_models.includes(model) || formData.compatible_models.includes(fullModel);
                                  return (
                                    <button
                                      key={model}
                                      type="button"
                                      onClick={() => {
                                        const currentModels = formData.compatible_models ? formData.compatible_models.split(',').map(m => m.trim()).filter(m => m) : [];
                                        if (isSelected) {
                                          const filtered = currentModels.filter(m => m !== model && m !== fullModel);
                                          handleChange('compatible_models', filtered.join(', '));
                                        } else {
                                          handleChange('compatible_models', [...currentModels, `${brand} ${model}`].join(', '));
                                        }
                                      }}
                                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                                        isSelected
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white hover:bg-blue-100 text-blue-700 border border-blue-200'
                                      }`}
                                    >
                                      {model}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    
                    <Input
                      id="compatible_models"
                      placeholder={t('createListing.compatible_models_placeholder')}
                      value={formData.compatible_models}
                      onChange={(e) => handleChange('compatible_models', e.target.value)}
                      data-testid="compatible-models-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sélectionnez les modèles suggérés ci-dessus ou saisissez manuellement (séparés par des virgules)
                    </p>
                  </div>

                  {/* Compatible Years */}
                  <div className="space-y-2">
                    <Label htmlFor="compatible_years">{t('createListing.compatible_years_label')}</Label>
                    
                    {/* Quick year range suggestions */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {['2020-2025', '2015-2020', '2010-2015', '2005-2010', '2000-2005', '1990-2000'].map(range => (
                        <button
                          key={range}
                          type="button"
                          onClick={() => handleChange('compatible_years', range)}
                          className={`px-3 py-1 rounded-full text-xs transition-colors ${
                            formData.compatible_years === range
                              ? 'bg-accent text-white'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                    
                    <Input
                      id="compatible_years"
                      placeholder={t('createListing.compatible_years_placeholder')}
                      value={formData.compatible_years}
                      onChange={(e) => handleChange('compatible_years', e.target.value)}
                      data-testid="compatible-years-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Ex: 2015-2020 ou 2018, 2019, 2020
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* OEM Reference */}
                    <div className="space-y-2">
                      <Label htmlFor="oem_reference">{t('createListing.oem_reference_label')}</Label>
                      <Input
                        id="oem_reference"
                        placeholder={t('createListing.oem_reference_placeholder')}
                        value={formData.oem_reference}
                        onChange={(e) => handleChange('oem_reference', e.target.value)}
                        data-testid="oem-reference-input"
                      />
                    </div>

                    {/* Aftermarket Reference */}
                    <div className="space-y-2">
                      <Label htmlFor="aftermarket_reference">{t('createListing.aftermarket_reference_label')}</Label>
                      <Input
                        id="aftermarket_reference"
                        placeholder={t('createListing.aftermarket_reference_placeholder')}
                        value={formData.aftermarket_reference}
                        onChange={(e) => handleChange('aftermarket_reference', e.target.value)}
                        data-testid="aftermarket-reference-input"
                      />
                    </div>
                  </div>

                  {/* Confiance & Traçabilité */}
                  <div className="mt-6 p-4 border rounded-lg bg-secondary/30">
                    <h4 className="font-medium mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-accent" />
                      {t('createListing.traceability_title')}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="part_origin">{t('createListing.part_origin_label')}</Label>
                        <Select value={formData.part_origin} onValueChange={(v) => handleChange('part_origin', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('createListing.part_origin_placeholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="casse">{t('createListing.part_origin_casse')}</SelectItem>
                            <SelectItem value="particulier">{t('createListing.part_origin_particulier')}</SelectItem>
                            <SelectItem value="professionnel">{t('createListing.part_origin_professionnel')}</SelectItem>
                            <SelectItem value="neuf">{t('createListing.part_origin_neuf')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehicle_mileage">{t('createListing.vehicle_mileage_label')}</Label>
                        <Input
                          id="vehicle_mileage"
                          type="number"
                          placeholder={t('createListing.vehicle_mileage_placeholder')}
                          value={formData.vehicle_mileage}
                          onChange={(e) => handleChange('vehicle_mileage', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">{t('createListing.vehicle_mileage_hint')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Caractéristiques véhicule donneur */}
                  <div className="mt-6 p-4 border rounded-lg bg-blue-50/50">
                    <h4 className="font-medium mb-4 flex items-center gap-2">
                      🚗 Véhicule donneur (optionnel)
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Améliore la recherche</span>
                    </h4>
                    <p className="text-xs text-muted-foreground mb-4">
                      Ces informations aident les acheteurs à trouver la pièce compatible avec leur véhicule
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {/* Type de carburant */}
                      <div className="space-y-2">
                        <Label>Type de carburant</Label>
                        <Select value={formData.fuel_type} onValueChange={(v) => handleChange('fuel_type', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {fuelTypes.map(ft => (
                              <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Boîte de vitesses */}
                      <div className="space-y-2">
                        <Label>Boîte de vitesses</Label>
                        <Select value={formData.gearbox} onValueChange={(v) => handleChange('gearbox', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {gearboxTypes.map(gt => (
                              <SelectItem key={gt.value} value={gt.value}>{gt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Type de carrosserie */}
                      <div className="space-y-2">
                        <Label>Type de carrosserie</Label>
                        <Select value={formData.body_type} onValueChange={(v) => handleChange('body_type', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {bodyTypes.map(bt => (
                              <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Couleur du véhicule */}
                      <div className="space-y-2">
                        <Label>Couleur du véhicule</Label>
                        <Select value={formData.vehicle_color} onValueChange={(v) => handleChange('vehicle_color', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {vehicleColors.map(vc => (
                              <SelectItem key={vc.value} value={vc.value}>{vc.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Code couleur */}
                      <div className="space-y-2">
                        <Label>Code couleur</Label>
                        <Input
                          placeholder="Ex: LY9T, 9H..."
                          value={formData.color_code}
                          onChange={(e) => handleChange('color_code', e.target.value)}
                        />
                      </div>

                      {/* Roues motrices */}
                      <div className="space-y-2">
                        <Label>Roues motrices</Label>
                        <Select value={formData.drive_type} onValueChange={(v) => handleChange('drive_type', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {driveTypes.map(dt => (
                              <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Position du volant */}
                      <div className="space-y-2">
                        <Label>Position du volant</Label>
                        <Select value={formData.steering_side} onValueChange={(v) => handleChange('steering_side', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {steeringSides.map(ss => (
                              <SelectItem key={ss.value} value={ss.value}>{ss.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* VIN */}
                      <div className="space-y-2 col-span-2">
                        <Label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.vin_available}
                            onChange={(e) => handleChange('vin_available', e.target.checked)}
                            className="rounded"
                          />
                          Numéro VIN disponible
                        </Label>
                        {formData.vin_available && (
                          <Input
                            placeholder="Ex: WVWZZZ3CZWE123456"
                            value={formData.vin_number}
                            onChange={(e) => handleChange('vin_number', e.target.value.toUpperCase())}
                            className="font-mono"
                            maxLength={17}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">{t('createListing.city_label')}</Label>
                  <Input
                    id="location"
                    placeholder="Paris"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    data-testid="location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">{t('createListing.postal_code_label')}</Label>
                  <Input
                    id="postal_code"
                    placeholder="75001"
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                    data-testid="postal-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">{t('createListing.region_label')}</Label>
                  <Select value={formData.region} onValueChange={(v) => handleChange('region', v)}>
                    <SelectTrigger data-testid="region-select">
                      <SelectValue placeholder={t('createListing.region_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t('createListing.photos_label')}</Label>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${images.length >= maxPhotos ? 'text-orange-500' : 'text-muted-foreground'}`}>
                      {t('createListing.photos_count', { count: images.length, max: maxPhotos })}
                    </span>
                    {photoLimit.is_pro && (
                      <span className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full">PRO</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('createListing.photos_hint', { max: maxPhotos })}
                </p>
                
                {/* Image Preview Grid */}
                {images.length > 0 && (
                  <div className="grid grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden border">
                        <img
                          src={image.url}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 w-6 h-6"
                          onClick={() => removeImage(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 bg-accent text-white text-xs px-2 py-1 rounded">
                            {t('createListing.main_photo')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {images.length < maxPhotos && (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-accent transition-colors">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
                      multiple
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      {uploadingImages ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-accent" />
                          <span className="text-sm text-muted-foreground">{t('createListing.photos_uploading')}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm font-medium">{t('createListing.photos_click_to_add')}</span>
                          <span className="text-xs text-muted-foreground">
                            {images.length}/{maxPhotos} {t('createListing.photos_added')}
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                )}

                {/* Buy Extra Photos Option */}
                {images.length >= maxPhotos && !photoLimit.is_pro && (
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                          <Camera className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('createListing.buy_extra_photos_title')}</p>
                          <p className="text-xs text-muted-foreground">{t('createListing.buy_extra_photos_desc')}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handleBuyExtraPhotos}
                        disabled={buyingPhotos}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        {buyingPhotos ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        {t('createListing.buy_extra_photos_btn')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Video */}
              <div className="space-y-4">
                <Label className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  {t('createListing.video_label')}
                </Label>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {videoLimit.is_extended || videoLimit.is_pro ? (
                      <>🎬 {t('createListing.video_limit_extended')} : <span className="font-medium text-green-600">{videoLimit.max_duration}s / {videoLimit.max_size_mb} Mo</span> {videoLimit.is_pro && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full ml-1">PRO</span>}</>
                    ) : (
                      <>🎬 {t('createListing.video_limit_free')} : <span className="font-medium">{videoLimit.max_duration}s / {videoLimit.max_size_mb} Mo</span></>
                    )}
                  </p>
                  {!videoLimit.is_extended && !videoLimit.is_pro && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleBuyExtendedVideo}
                      disabled={buyingVideo}
                      className="text-xs"
                    >
                      {buyingVideo ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      {t('createListing.video_buy_extended')}
                    </Button>
                  )}
                </div>
                
                {/* Video Preview */}
                {videoUrl && (
                  <div className="relative rounded-lg overflow-hidden border bg-black">
                    <video
                      src={videoUrl}
                      controls
                      className="w-full max-h-64"
                    >
                      {t('listingDetail.video_not_supported')}
                    </video>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeVideo}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Video Upload Button */}
                {!videoUrl && (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-accent transition-colors">
                    <input
                      type="file"
                      ref={videoInputRef}
                      onChange={handleVideoUpload}
                      accept="video/mp4,video/webm,video/quicktime,video/mov"
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer">
                      {uploadingVideo ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-accent" />
                          <span className="text-sm text-muted-foreground">{t('createListing.video_uploading')}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Video className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm font-medium">{t('createListing.video_click_to_add')}</span>
                          <span className="text-xs text-muted-foreground">
                            Max {videoLimit.max_size_mb} Mo • {videoLimit.max_duration}s • {t('createListing.video_formats')}
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-accent hover:bg-accent/90 btn-primary"
                  disabled={loading || uploadingImages || uploadingVideo || user?.credits <= 0}
                  data-testid="submit-listing-btn"
                >
                  {loading ? t('createListing.publishing') : t('createListing.submit_btn')}
                </Button>
              </div>

              {user?.credits <= 0 && (
                <p className="text-center text-destructive text-sm">
                  {t('createListing.no_credits_error')}{' '}
                  <Link to="/tarifs" className="underline">{t('createListing.buy_pack')}</Link>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Modal de sélection d'article entrepôt */}
      <Dialog open={showWarehouseModal} onOpenChange={setShowWarehouseModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="warehouse-selection-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-accent" />
              {t('createListing.warehouse_modal_title')}
            </DialogTitle>
          </DialogHeader>
          
          {/* Barre de recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('createListing.warehouse_search_placeholder')}
              value={warehouseSearch}
              onChange={(e) => setWarehouseSearch(e.target.value)}
              className="pl-10"
              data-testid="warehouse-search-input"
            />
          </div>

          {/* Liste des articles */}
          <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            {loadingWarehouse ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : filteredWarehouseItems.length > 0 ? (
              <div className="space-y-2">
                {filteredWarehouseItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleSelectWarehouseItem(item)}
                    className="p-4 border rounded-lg cursor-pointer hover:border-accent hover:bg-accent/5 transition-all"
                    data-testid={`warehouse-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {item.reference_oem && (
                              <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
                                OEM: {item.reference_oem}
                              </span>
                            )}
                            {item.brand && <Badge variant="outline">{item.brand}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-accent">{item.selling_price ? `${item.selling_price}€` : '-'}</p>
                        <p className="text-xs text-muted-foreground">Stock: {item.quantity}</p>
                      </div>
                    </div>
                    {item.notes && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{item.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {warehouseSearch 
                    ? t('createListing.warehouse_no_search_results')
                    : t('createListing.warehouse_no_items')}
                </p>
                {!warehouseSearch && (
                  <Link to="/entrepot">
                    <Button variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('createListing.warehouse_add_items')}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowWarehouseModal(false)}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
